/**
 * MirrorService - Pure business logic for mirroring waypoints
 * 
 * This service contains all the mirror calculations without any React dependencies.
 * It can be tested in isolation and used in different contexts (hooks, workers, etc.)
 */

import {
  featureToWorld,
  worldToFeature,
  mirrorPositionAcrossPlane,
  mirrorRotationAcrossPlane
} from '../utils/coordinateTransforms.js';

// Import types for JSDoc
// eslint-disable-next-line no-unused-vars
import { Types } from '../types/index.js';

/**
 * @typedef {Object} MirroredCoords
 * @property {Types.Position} position
 * @property {Types.Rotation} rotation
 */

/**
 * Mirror a single position/rotation pair across a plane in a feature's coordinate system
 * @param {Types.Position} position - Position to mirror
 * @param {Types.Rotation} rotation - Rotation to mirror
 * @param {Types.Feature} sourceFeature - The feature the coordinates are relative to
 * @param {Types.Feature} mirrorFeature - The feature defining the mirror plane
 * @param {string} plane - The plane to mirror across ('xy', 'xz', 'yz')
 * @returns {MirroredCoords} Mirrored coordinates in source feature's frame
 */
export function mirrorCoordinates(position, rotation, sourceFeature, mirrorFeature, plane) {
  // Convert from source feature coords to world coords
  const worldCoords = featureToWorld(position, rotation, sourceFeature);
  
  // Convert from world coords to mirror feature's local coords
  const inMirrorFeature = worldToFeature(worldCoords.position, worldCoords.rotation, mirrorFeature);
  
  // Apply mirror in the mirror feature's coordinate system
  const mirroredInMirrorFeature = {
    position: mirrorPositionAcrossPlane(inMirrorFeature.position, plane),
    rotation: mirrorRotationAcrossPlane(inMirrorFeature.rotation, plane)
  };
  
  // Convert back to world coords
  const mirroredWorld = featureToWorld(
    mirroredInMirrorFeature.position,
    mirroredInMirrorFeature.rotation,
    mirrorFeature
  );
  
  // Convert back to source feature's local coords
  return worldToFeature(mirroredWorld.position, mirroredWorld.rotation, sourceFeature);
}

/**
 * Mirror a waypoint across a plane
 * @param {Waypoint} waypoint - The waypoint to mirror
 * @param {Feature} waypointFeature - The feature the waypoint is relative to
 * @param {Feature} mirrorFeature - The feature defining the mirror plane
 * @param {string} plane - The plane to mirror across ('xy', 'xz', 'yz')
 * @returns {Object} Object with mirrored position, rotation, and optionally viaPoint
 */
export function mirrorWaypoint(waypoint, waypointFeature, mirrorFeature, plane) {
  // Mirror main position/rotation
  const mirrored = mirrorCoordinates(
    waypoint.position,
    waypoint.rotation,
    waypointFeature,
    mirrorFeature,
    plane
  );
  
  const result = {
    position: mirrored.position,
    rotation: mirrored.rotation,
    viaPoint: null
  };
  
  // Mirror via-point if it exists
  if (waypoint.viaPoint) {
    const mirroredVia = mirrorCoordinates(
      waypoint.viaPoint.position,
      waypoint.viaPoint.rotation,
      waypointFeature,
      mirrorFeature,
      plane
    );
    result.viaPoint = {
      position: mirroredVia.position,
      rotation: mirroredVia.rotation
    };
  }
  
  return result;
}

/**
 * Determine which waypoint indices to mirror based on scope
 * @param {string} scope - 'all', 'selected', or 'node'
 * @param {Array<Waypoint>} waypoints - All waypoints
 * @param {Set<number>} selectedIndices - Currently selected waypoint indices
 * @param {Array<Object>} nodes - Node data with indices
 * @param {number} nodeIndex - Index of node (for 'node' scope)
 * @returns {{indices: Array<number>, error: string|null}} Result with indices or error
 */
export function getWaypointIndicesToMirror(scope, waypoints, selectedIndices, nodes, nodeIndex) {
  switch (scope) {
    case 'all':
      return { indices: waypoints.map((_, i) => i), error: null };
      
    case 'selected':
      if (selectedIndices.size === 0) {
        return { 
          indices: [], 
          error: 'No waypoints selected. Use Ctrl+Click to select multiple waypoints.' 
        };
      }
      return { indices: Array.from(selectedIndices), error: null };
      
    case 'node':
      if (nodeIndex >= 0 && nodeIndex < nodes.length) {
        return { indices: nodes[nodeIndex].indices, error: null };
      }
      return { indices: [], error: 'Invalid node selected' };
      
    default:
      return { indices: [], error: 'Please select a scope' };
  }
}

/**
 * Group waypoints by their parent node
 * @param {Array<Waypoint>} waypoints - All waypoints
 * @param {Array<number>} indices - Indices to group
 * @returns {Map<Object, Array<number>>} Map of parentNode to waypoint indices
 */
export function groupWaypointsByParentNode(waypoints, indices) {
  const groups = new Map();
  
  for (const index of indices) {
    const waypoint = waypoints[index];
    const parentNode = waypoint.parentNode;
    
    if (!groups.has(parentNode)) {
      groups.set(parentNode, []);
    }
    groups.get(parentNode).push(index);
  }
  
  return groups;
}
